/**
 * What one assistant turn actually is, in order.
 *
 * The first pass of this chat held a turn as ONE STRING. That is fine while the only thing arriving is
 * prose and fatal the moment anything else does: a turn is really a sequence — thought for nine
 * seconds, read three Linear issues, wrote a paragraph, asked a question, wrote the artifact — and the
 * *order* is most of the meaning. A string cannot hold it, and reconstructing paragraphs downstream by
 * splitting on `\n{2,}` was the tell.
 *
 * So a turn is an ordered `TurnBlock[]`, appended to as events arrive and rebuilt the same shape from
 * `PlanningChatMessage.parts` on reload. Both paths land on one array so the transcript renders once.
 *
 * The blocks are DATA. No React, no handlers, no theme — the renderer supplies those. That is what
 * lets the same array come from a live stream or from history without either caring.
 */
import type {
  ActivityListRowProps,
  ActivityTool,
  AgentLifecycle,
  SkeletonBar,
} from '~/design-system/components/v1_5/Chat';
import type { ClarificationItem, MessagePart } from '~/types/planning';

/** A step inside an activity card. v1.5's row, minus the two things the renderer owns. */
export type TurnListRow = Omit<ActivityListRowProps, 'theme' | 'className'>;

/**
 * The body under an activity card's header.
 *
 * Mirrors v1.5's `ActivityBody` and deliberately drops `ActivityChoice.onSelect` — a handler in a data
 * model is a handler that has to be rebuilt on every rehydrate, and answering is Track A's business.
 */
export type TurnActivityBody =
  | { kind: 'none' }
  | { kind: 'list'; rows: TurnListRow[] }
  | { kind: 'skeleton'; bars?: SkeletonBar[] }
  | { kind: 'thumbnails'; fills: string[] }
  | { kind: 'explanation'; text: string };

/**
 * One thing that happened in a turn.
 *
 * `id` is stable within a turn and is the React key. For a live block it is minted from a counter
 * rather than a timestamp, because two events can land in the same millisecond and a duplicate key
 * silently drops one of them.
 */
export type TurnBlock =
  /**
   * A paragraph of the answer. Sealed by `textBoundary`, which is why that event stopped being dropped.
   * `open` is set only on a paragraph this stream wrote: `true` while it is still being written, `false`
   * once `textBoundary`, `done` or any other block closed it. A paragraph rebuilt from history has none.
   */
  | { kind: 'prose'; id: string; text: string; open?: boolean }
  /**
   * Reasoning. `live` while it is still arriving — shown verbatim in a rolling window — and settled
   * afterwards into the collapsible "Thought for N sec" recap. Both are the same block; only the flag
   * moves, so the text does not jump between two components as it settles.
   */
  | { kind: 'thinking'; id: string; text: string; durationSec?: number; live: boolean }
  /** One tool or one agent action, with its header and its body. */
  | {
      kind: 'activity';
      id: string;
      tool: ActivityTool;
      label: string;
      meta?: string;
      /** Canonical resource identity; the workspace resolves it to a directory row. */
      resourceKey?: string;
      resourceTitle?: string;
      lifecycle: AgentLifecycle;
      body: TurnActivityBody;
      /** Machinery (skill loads, handoffs) — live only, dropped from the settled recap. */
      transient?: boolean;
    }
  /**
   * The agent asked. Carried on the turn so the transcript can show the "asking" beat while the
   * composer holds the card, and the answered summary afterwards.
   */
  | {
      kind: 'question';
      id: string;
      requestId: string;
      items: ClarificationItem[];
      status: 'asking' | 'answered' | 'skipped';
    }
  /** An artifact this turn touched, by canvas slide id. The card resolves its own title. */
  | { kind: 'artifact'; id: string; slideId: number }
  /** Work that outlives the turn — `TaskCard`. */
  | { kind: 'task'; id: string; label: string; state: 'running' | 'done' | 'external'; brand?: 'github' };

/** A cache-only part that preserves the exact v1.5 block while the server finishes its own save. */
export const DECK15_BLOCK_PART_TYPE = 'DECK15_BLOCK' as const;

/**
 * The live pane and the settled pane read the same message cache. Keep the richer blocks in that
 * cache too, so clearing `live` after `done` does not erase the thought/activity timeline before the
 * server's next history read arrives.
 */
export function messagePartsFromTurnBlocks(blocks: TurnBlock[]): MessagePart[] {
  return blocks.map((block) =>
    block.kind === 'prose'
      ? { type: 'TEXT', content: block.text, specialist: null }
      : { type: DECK15_BLOCK_PART_TYPE, content: JSON.stringify(block), specialist: null },
  );
}

/* ── the live accumulator ───────────────────────────────────────────────────────────────────── */

/**
 * A turn under construction.
 *
 * Held as a mutable object rather than rebuilt immutably per delta because a streaming reply can land
 * a hundred content chunks a second, and the alternative is a hundred array copies. The React-facing
 * copy is taken once per paint by `snapshot()`.
 */
export class TurnBuilder {
  private blocks: TurnBlock[] = [];
  private seq = 0;

  /** The prose block still open, if the last thing to arrive was text. */
  private openProse: { kind: 'prose'; id: string; text: string; open?: boolean } | null = null;

  /** The thinking block still accumulating, if reasoning is arriving. */
  private openThinking: { kind: 'thinking'; id: string; text: string; live: boolean } | null = null;

  private startedAt = 0;
  private thinkingStartedAt = 0;

  /** `nowMs` is injected so the caller owns the clock; tests do not need a fake timer. */
  constructor(nowMs: number) {
    this.startedAt = nowMs;
  }

  private nextId(prefix: string): string {
    this.seq += 1;
    return `${prefix}-${this.seq}`;
  }

  /**
   * Anything that is not more prose ends the paragraph.
   *
   * Called by every non-text event, which is what keeps a tool row from landing INSIDE a sentence. The
   * backend also sends `textBoundary` explicitly for a paragraph break with no event between.
   */
  sealProse(): void {
    if (this.openProse && !this.openProse.text.trim()) {
      // An empty paragraph is a boundary that arrived twice. Drop it rather than paint 22px of nothing.
      this.blocks = this.blocks.filter((block) => block !== this.openProse);
    }
    if (this.openProse) this.openProse.open = false;
    this.openProse = null;
  }

  /** Reasoning stops accumulating and becomes the settled recap. */
  sealThinking(nowMs: number): void {
    if (!this.openThinking) return;
    const settled = this.openThinking;
    settled.live = false;
    (settled as TurnBlock & { durationSec?: number }).durationSec = Math.max(
      1,
      Math.round((nowMs - this.thinkingStartedAt) / 1000),
    );
    if (!settled.text.trim()) this.blocks = this.blocks.filter((block) => block !== settled);
    this.openThinking = null;
  }

  /** Both, in the order every handler wants them: thinking settles, then the paragraph closes. */
  private seal(nowMs: number): void {
    this.sealThinking(nowMs);
    this.sealProse();
  }

  prose(chunk: string, nowMs: number): void {
    this.sealThinking(nowMs);
    if (!this.openProse) {
      this.openProse = { kind: 'prose', id: this.nextId('prose'), text: '', open: true };
      this.blocks.push(this.openProse);
    }
    this.openProse.text += chunk;
  }

  thinking(chunk: string, nowMs: number): void {
    this.sealProse();
    if (!this.openThinking) {
      this.openThinking = { kind: 'thinking', id: this.nextId('think'), text: '', live: true };
      this.thinkingStartedAt = nowMs;
      this.blocks.push(this.openThinking as TurnBlock);
    }
    this.openThinking.text += chunk;
  }

  /**
   * An activity card, keyed so a second event for the same tool updates in place.
   *
   * The stream sends a tool's progress as a series of whole snapshots, not deltas — `linearAction`
   * arrives three times with a growing issue list — so an append-only model would stack three cards
   * for one action.
   */
  activity(key: string, block: Omit<Extract<TurnBlock, { kind: 'activity' }>, 'kind' | 'id'>, nowMs: number): void {
    this.seal(nowMs);
    const at = this.blocks.findIndex((b) => b.kind === 'activity' && b.id === `act-${key}`);
    const next: TurnBlock = { kind: 'activity', id: `act-${key}`, ...block };
    if (at === -1) this.blocks.push(next);
    else this.blocks[at] = next;
  }

  question(requestId: string, items: ClarificationItem[], status: 'asking' | 'answered' | 'skipped', nowMs: number): void {
    this.seal(nowMs);
    const at = this.blocks.findIndex((b) => b.kind === 'question' && b.requestId === requestId);
    const next: TurnBlock = { kind: 'question', id: `q-${requestId}`, requestId, items, status };
    if (at === -1) this.blocks.push(next);
    else this.blocks[at] = next;
  }

  /** Deduped: the same slide named by two events is one card. */
  artifact(slideId: number, nowMs: number): void {
    this.seal(nowMs);
    if (this.blocks.some((b) => b.kind === 'artifact' && b.slideId === slideId)) return;
    this.blocks.push({ kind: 'artifact', id: `art-${slideId}`, slideId });
  }

  /** Seconds since the turn opened. The live row's clock. */
  elapsedSec(nowMs: number): number {
    return Math.max(0, Math.round((nowMs - this.startedAt) / 1000));
  }

  /** Is reasoning arriving right now — the live indicator's gate. */
  get thinkingLive(): boolean {
    return this.openThinking !== null;
  }

  /**
   * The array React reads. A fresh outer array every call so `useSyncExternalStore` and `memo` see a
   * change; the blocks themselves are shared, which is what keeps a hundred-chunk paint cheap.
   */
  snapshot(): TurnBlock[] {
    return [...this.blocks];
  }

  /**
   * The turn is over: nothing is still accumulating, and nothing is still running.
   *
   * Two things settle here that no event settles for us.
   *
   * **Visible process rows remain in the recap.** The agent narrates itself with `action` events —
   * "Loading a skill", "Reading the canvas" — and those rows are part of the timeline the user asked
   * to keep. The `transient` flag is live-stream metadata only; it is removed when the row settles.
   *
   * **Anything still `running` becomes `complete`.** The stream has no per-tool completion event for
   * several of the cards — `editCanvasDispatch` in particular fires once, on the way in, and nothing
   * ever says the edit landed. Left alone the artifact card spins forever on a finished turn. `done`
   * IS the completion signal for everything still open; a block that genuinely failed already says
   * `failed` and is left alone.
   */
  finish(nowMs: number): TurnBlock[] {
    this.seal(nowMs);
    this.blocks = this.blocks.map((block) => {
      if (block.kind !== 'activity') return block;

      const withoutLiveOnlyMetadata = { ...block };
      delete withoutLiveOnlyMetadata.transient;
      return {
        ...withoutLiveOnlyMetadata,
        lifecycle:
          block.lifecycle === 'running' || block.lifecycle === 'updating'
            ? ('complete' as const)
            : block.lifecycle,
        // A skeleton is a live preview, not a finished report. The completed row itself remains
        // visible, but it must not keep looking like it is still generating forever.
        body: block.body.kind === 'skeleton' ? { kind: 'none' as const } : block.body,
      };
    });
    return this.snapshot();
  }
}

/** `0:04`, `1:14` — the live row's clock, which is 12/20 hint and never a chevron's neighbour. */
export function elapsedLabel(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
